import { useEffect, useState } from 'react';
import { supabase, Page, SocialLink, HeaderSettings, MenuItem, FooterSettings } from '../lib/supabase';
import { Menu, X } from 'lucide-react';
import WidgetRenderer from './WidgetRenderer';

type PublicPageViewProps = {
  pageSlug?: string;
};

export default function PublicPageView({ pageSlug = 'main' }: PublicPageViewProps) {
  const [page, setPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
  const [headerSettings, setHeaderSettings] = useState<HeaderSettings | null>(null);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [footerSettings, setFooterSettings] = useState<FooterSettings | null>(null);

  useEffect(() => {
    updateMetaTags();
  }, [page]);

  function updateMetaTags() {
    if (!page) return;

    document.title = page.seo_title || page.title;

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', page.seo_description || '');
    } else if (page.seo_description) {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = page.seo_description;
      document.head.appendChild(meta);
    }

    if (page.seo_keywords) {
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute('content', page.seo_keywords);
      } else {
        const meta = document.createElement('meta');
        meta.name = 'keywords';
        meta.content = page.seo_keywords;
        document.head.appendChild(meta);
      }
    }

    const metaRobots = document.querySelector('meta[name="robots"]');
    const robotsContent = `${page.seo_index ? 'index' : 'noindex'}, ${page.seo_follow ? 'follow' : 'nofollow'}`;
    if (metaRobots) {
      metaRobots.setAttribute('content', robotsContent);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'robots';
      meta.content = robotsContent;
      document.head.appendChild(meta);
    }

    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      existingCanonical.remove();
    }

    if (page.is_canonical && page.canonical_url) {
      const canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = page.canonical_url;
      document.head.appendChild(canonical);
    }
  }

  useEffect(() => {
    loadPage();
    loadSocialLinks();
    loadHeaderSettings();
    loadMenuItems();
    loadFooterSettings();
  }, [pageSlug]);

  useEffect(() => {
    const contentDiv = document.querySelector('.page-content');
    if (contentDiv) {
      const iframes = contentDiv.querySelectorAll('iframe');
      iframes.forEach((iframe) => {
        if (!iframe.hasAttribute('sandbox')) {
          iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox');
        }
        if (!iframe.hasAttribute('allow')) {
          iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
        }
      });
    }
  }, [page]);

  async function loadPage() {
    const { data } = await supabase
      .from('pages')
      .select('*')
      .eq('slug', pageSlug)
      .eq('is_active', true)
      .maybeSingle();

    if (data) {
      setPage(data);
    }
    setLoading(false);
  }

  async function loadSocialLinks() {
    const { data } = await supabase
      .from('social_links')
      .select('*')
      .eq('is_active', true)
      .order('display_order');

    if (data) {
      setSocialLinks(data);
    }
  }

  async function loadHeaderSettings() {
    const { data } = await supabase
      .from('header_settings')
      .select('*')
      .limit(1)
      .maybeSingle();

    if (data) {
      setHeaderSettings(data);
    }
  }

  async function loadMenuItems() {
    const { data } = await supabase
      .from('menu_items')
      .select('*')
      .eq('is_active', true)
      .order('display_order');

    if (data) {
      setMenuItems(data);
    }
  }

  async function loadFooterSettings() {
    const { data } = await supabase
      .from('footer_settings')
      .select('*')
      .limit(1)
      .maybeSingle();

    if (data) {
      setFooterSettings(data);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Страница не найдена</h1>
          <p className="text-xl text-gray-600">Запрошенная страница не существует</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Open Sans', sans-serif" }}>
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-2 sm:py-3">
          <div className="max-w-7xl mx-auto px-4 flex justify-center items-center">
            <div className="flex gap-3 sm:gap-6 text-sm sm:text-base flex-wrap justify-center">
              {socialLinks.map((link) => {
                return (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-gray-200 transition-colors whitespace-nowrap"
                  >
                    {link.name}
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        <div className="py-2 sm:py-3" style={{ backgroundColor: headerSettings?.background_color || '#FFE9FC' }}>
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                <a href={headerSettings?.logo_link || 'https://seo-nova.com'} className="flex-shrink-0">
                  <img
                    src={headerSettings?.logo_url || '/Logo 72 px_100Kb.jpg'}
                    alt={headerSettings?.logo_alt || 'Logo'}
                    title={headerSettings?.logo_title || ''}
                    className="object-contain h-12 sm:h-16 md:h-[72px] w-auto"
                  />
                </a>
                <div className="min-w-0 flex-1">
                  <a
                    href={headerSettings?.logo_link || 'https://seo-nova.com'}
                    className="block truncate"
                    style={{
                      color: headerSettings?.title_color || '#000066',
                      fontFamily: headerSettings?.title_font_family || "'Open Sans', sans-serif",
                      fontSize: 'clamp(18px, 4vw, ' + (headerSettings?.title_font_size || '30px') + ')',
                      fontWeight: 700,
                      lineHeight: 1.2,
                    }}
                  >
                    {headerSettings?.title_text || 'SEO-NOVA'}
                  </a>
                  <p
                    className="mt-0.5 sm:mt-1 truncate"
                    style={{
                      color: headerSettings?.subtitle_color || '#000066',
                      fontSize: 'clamp(10px, 2vw, ' + (headerSettings?.subtitle_font_size || '12px') + ')',
                      lineHeight: 1.3,
                    }}
                  >
                    {headerSettings?.subtitle_text || 'Быстрые и простые рецепты'}
                  </p>
                </div>
              </div>

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 flex-shrink-0 ml-2"
                style={{ color: headerSettings?.menu_color || '#000066' }}
                aria-label="Меню"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>

              <nav className="hidden lg:flex gap-6 items-center ml-6">
                {menuItems.map((item) => (
                  <a
                    key={item.id}
                    href={item.url}
                    className="font-semibold transition-colors whitespace-nowrap"
                    style={{
                      color: headerSettings?.menu_color || '#000066',
                      fontSize: headerSettings?.menu_font_size || '16px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = headerSettings?.menu_hover_color || '#ef4444'}
                    onMouseLeave={(e) => e.currentTarget.style.color = headerSettings?.menu_color || '#000066'}
                  >
                    {item.label}
                  </a>
                ))}
              </nav>
            </div>

            {mobileMenuOpen && (
              <nav className="lg:hidden mt-4 pb-4 border-t pt-4">
                <div className="flex flex-col gap-3">
                  {menuItems.map((item) => (
                    <a
                      key={item.id}
                      href={item.url}
                      className="font-semibold py-2"
                      style={{
                        color: headerSettings?.menu_color || '#000066',
                        fontSize: headerSettings?.menu_font_size || '16px'
                      }}
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              </nav>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-4 sm:py-6 md:py-8">
        <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 lg:gap-8">
          <aside className="hidden lg:block w-80 flex-shrink-0">
            <WidgetRenderer position="sidebar" />
          </aside>

          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">{page.title}</h1>
            <div
              className="prose prose-sm sm:prose-base lg:prose-lg max-w-none page-content"
              dangerouslySetInnerHTML={{ __html: page.content }}
            />
          </div>
        </div>
      </main>

      <footer className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-6 sm:py-8 mt-8 sm:mt-12">
        <div className="max-w-7xl mx-auto px-4">
          {footerSettings?.content && (
            <div
              className="mb-4 sm:mb-6 prose prose-sm sm:prose-base prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: footerSettings.content }}
            />
          )}
          <div className="border-t border-blue-700 pt-4 sm:pt-6 mt-4 sm:mt-6 text-center">
            <div
              className="text-xs sm:text-sm text-blue-100 footer-content"
              dangerouslySetInnerHTML={{
                __html: footerSettings?.copyright_text || `© ${new Date().getFullYear()} SEO-NOVA. Все права защищены.`
              }}
            />
          </div>
        </div>
      </footer>
    </div>
  );
}
